import React, { forwardRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useField, useForm } from "react-final-form"; // Import useForm to access form methods
import {
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
} from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import ClearIcon from "@material-ui/icons/Clear";
import { required } from "react-admin";

interface ICustomMonthPickerInput {
  name: string;
  label?: string;
  disableValue?: boolean;
  showClearButton?: boolean;
  placeholderValue?: string;
  year?: number; // Adding props for year initialization
  month?: number; // Adding props for month initialization
  [key: string]: any;
}

const useStyles = makeStyles({
  root: {
    marginTop: "8px",
    marginBottom: "8px",
    width: "100%",
    backgroundColor: "#FFFFFF",
  },
  override: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": {
        borderColor: "#EAEAEA !important",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA !important",
      },
    },
  },
  picker: {
    "& .react-datepicker-wrapper": {
      width: "100%",
    },
    "& .react-datepicker-popper": {
      zIndex: 10,
    },
  },
});

const CustomMonthPickerInput = ({
  name,
  label,
  disableValue,
  placeholderValue,
  year, // Props to receive the initial year
  month, // Props to receive the initial month
  showClearButton,
  isRequired,
  minMonth,
  minYear,
  maxMonth,
  maxYear
}: ICustomMonthPickerInput) => {
  const classes = useStyles();
  const form = useForm(); // Access form methods to update Month and Year

  const {
    input: { onChange, value },
  } = useField(name, {
    validate: isRequired ? required() : undefined,
  });

  // Convert the month and year to a date object for initialization
  const initialDate = month && year ? new Date(year, month - 1, 1) : new Date();

  const [date, setDate] = useState(initialDate);

  // Compute minDate and maxDate from minMonth/year and maxMonth/year
  const computedMinDate =
    minMonth && minYear
      ? new Date(minYear, minMonth - 1, 1)
      : null;

  const computedMaxDate =
    maxMonth && maxYear
      ? new Date(maxYear, maxMonth - 1, 1)
      : null;

  // Use useEffect to update date state if month or year changes externally
  useEffect(() => {
    if (month && year) {
      const newDate = new Date(year, month - 1, 1);
      setDate(newDate);
      handleDateChange(newDate);
    }
  }, [month, year]);

  // Function to check if date is within the allowed range
  const isDateInRange = (date: Date) => {
    // If a minimum date is defined and the selected date is before it, it's invalid
    if (computedMinDate && date < computedMinDate) return false;

    // If a maximum date is defined and the selected date is after it, it's invalid
    if (computedMaxDate && date > computedMaxDate) return false;

    // Date is within range
    return true;
  };

  const handleDateChange = (selectedDate: Date) => {
    // Set to the first day of the selected month to avoid "Invalid date"
    const correctedDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1,
    );

    // If the corrected date is out of range, ignore the change
    if (!isDateInRange(correctedDate)) {
      return;
    }

    setDate(correctedDate);
    onChange(moment(correctedDate).format("MM/yyyy"));

    // Extract the month and year and update the form fields
    const month = correctedDate.getMonth() + 1; // +1 because getMonth() returns 0-11
    const year = correctedDate.getFullYear();

    // Update form fields for Month and Year
    form.change("month", month);
    form.change("year", year);
  };

  const handleClearDate = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the popup from opening

    setDate(null); // Reset date to null
    onChange(null); // Clear form field value
    form.change("month", null); // Clear month field
    form.change("year", null); // Clear year field
  };

  const CustomInput = forwardRef(({ onClick }: any, ref: any) => (
    <div className={classes.override}>
      <TextField
        className={classes.root}
        variant="filled"
        size="small"
        label={label}
        value={
          placeholderValue
            ? placeholderValue
            : value
            ? moment(value, "MM/yyyy").format("MM/yyyy")
            : ""
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" onClick={onClick}>
                <CalendarTodayIcon />
              </IconButton>
              {value && showClearButton && (
                <IconButton edge="end" onClick={handleClearDate}>
                  <ClearIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        onClick={onClick}
        ref={ref}
        disabled={disableValue}
      />
    </div>
  ));

  return (
    <div className={classes.picker}>
      <DatePicker
        selected={date}
        onChange={handleDateChange}
        showMonthYearPicker
        dateFormat="MM/yyyy"
        customInput={<CustomInput />}
        disabled={disableValue}
        minDate={computedMinDate}
        maxDate={computedMaxDate}
        todayButton="Today"
      />
    </div>
  );
};

export default CustomMonthPickerInput;
